import styled from '@emotion/styled'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { useEffect, useState, useMemo } from 'react'
import Video from '../Video'
import Link from '../../Link'
import { debounce } from 'lodash'

const MaxHeightImage = styled(GatsbyImage)`
  width: 100%;
  aspect-ratio: ${(props) => props.aspectratio && props.aspectratio};

  /* Smooth image loading transitions */
  img {
    transition: opacity 0.2s ease-in-out;
  }
`
const MaxHeightVideo = styled(Video)`
`
const ImageContainer = styled.div`
  @media (min-width: 769px) {
    max-width: ${(props) => props.maxwidth ? '80%' : '100%'};
    height: ${(props) => props.height ? `${props.height}vh` : 'auto'};;
    margin: 0 auto;
  }
  @media (max-width: 768px) {
    max-width: 100%;
    height: auto;
  }
`
const HeaderCopyHeading = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  padding: 10px;
  border-radius: 5px;
  z-index: 2;
  max-width: 1200px;
  width: 100vw;
  font-family: ${(props) => props.fontFamily ? `${props.fontFamily}` : ''};;
  font-size: ${(props) => props.fontSize ? `${props.fontSize}px` : '41px'};;
  font-weight: ${(props) => props.fontWeight ? `${props.fontWeight}` : '400'};;
  color: ${(props) => props.textColor ? `${props.textColor}` : 'white'};;
  font-style: ${(props) => props.fontFamily === "PPEditorialNew" ? `italic` : 'normal'};;

  @media (max-width: 768px) {
    font-size: 32px;
    width: 80vw;
  }
`
const StaticHeaderImage = styled.img`
  width: 100%;
  height: 100%;
  ${(props) => (props.padding ? 'margin: 8%;width: 34%;' : '')}
  @media (max-width: 1025px) {
    height: calc(100vh - 110px);
    width: 100%;
    ${(props) => (props.padding ? 'width: 90%;margin: 5%;' : '')}
    ${(props) => (props.noMobile ? 'display: none;' : '')}
    ${(props) => (props.fullLength ? 'width: 100%;' : '')}
  }
  object-fit: cover;
  
  /* Smooth image loading transitions */
  transition: opacity 0.2s ease-in-out;
`
const TopLevelContainer = styled.div`
  position: relative;
  margin: ${(props) => props.maxwidth ? '64px' : ''};

  @media (max-width: 1025px) {
    margin: ${(props) => props.maxwidth ? '16px' : ''};
  }
`

const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState('desktop');

  useEffect(() => {
    const getBreakpoint = (width) => {
      if (width <= 768) return 'mobile';
      if (width <= 1024) return 'tablet';
      return 'desktop';
    };

    const handleResize = debounce(() => {
      const currentBreakpoint = getBreakpoint(window.innerWidth);
      setBreakpoint(prev => prev !== currentBreakpoint ? currentBreakpoint : prev);
    }, 100);

    // Set initial breakpoint
    setBreakpoint(getBreakpoint(window.innerWidth));
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};

const FullWidthImage = React.memo(({ data }) => {
  const filename = data?.OptionalMobileImage?.name?.slice(0, data?.OptionalMobileImage?.name?.lastIndexOf('.'));
  const breakpoint = useBreakpoint();

  // Memoize image data to prevent unnecessary re-renders
  const imageData = useMemo(() => {
    if (!data.Image) return null;
    
    return {
      desktop: data.Image,
      mobile: data.OptionalMobileImage,
      isVideo: data.Image?.mime?.includes('video'),
      isStatic: data.Image?.isStatic
    };
  }, [data.Image, data.OptionalMobileImage]);

  return (
    <Link to={data?.linkTo} noLink={data?.linkTo === null}>
      <div id={filename} />
      <TopLevelContainer maxwidth={data.MaxWidthImage}>
        <ImageContainer maxwidth={data.MaxWidthImage} height={data.height}>
          {imageData?.isStatic ? (
            <StaticHeaderImage src={breakpoint === 'mobile' || breakpoint === 'tablet' ? imageData.mobile?.url : imageData.desktop?.url} />
          ) : (
            <>
              {breakpoint === 'desktop' ? (
                <>
                  {data.Image?.mime.includes('video') ? (
                    <MaxHeightVideo
                      padding={data.ImagePadding}
                      alt={data.Image?.alternativeText || data.Image?.name}
                      src={data.Image?.file.publicURL}
                      loading={data?.LazyLoading === false ? 'eager' : 'lazy'}
                    />
                  ) : (
                    imageData?.desktop?.file?.childImageSharp?.gatsbyImageData ? (
                      <MaxHeightImage
                        style={{ padding: 0, margin: 0 }}
                        height={data.height}
                        objectPosition="center bottom"
                        alt={imageData?.desktop?.alternativeText || imageData?.desktop?.name}
                        aspectratio={imageData?.desktop?.file?.childImageSharp?.gatsbyImageData?.width / imageData?.desktop?.file?.childImageSharp?.gatsbyImageData?.height}
                        image={imageData?.desktop?.file?.childImageSharp?.gatsbyImageData}
                        loading={data?.LazyLoading === false ? 'eager' : 'lazy'}
                      />
                    ) : (
                      <></>
                    )
                  )}
                </>
              ) : (
                <>
                  {imageData?.mobile?.mime?.includes('video') ? (
                    <MaxHeightVideo
                      padding={data.ImagePadding}
                      alt={imageData?.mobile?.alternativeText || imageData?.mobile?.name}
                      src={imageData?.mobile?.file?.publicURL}
                      loading={data?.LazyLoading === false ? 'eager' : 'lazy'}
                    />
                  ) : (
                    imageData?.mobile?.file?.childImageSharp?.gatsbyImageData ? (
                      <MaxHeightImage
                        loading={data?.LazyLoading === false ? 'eager' : 'lazy'}
                        style={{ padding: 0, margin: 0 }}
                        height={data.height}
                        objectPosition="center bottom"
                        alt={imageData?.mobile?.alternativeText || imageData?.mobile?.name}
                        aspectratio={imageData?.mobile?.file?.childImageSharp?.gatsbyImageData?.width / imageData?.mobile?.file?.childImageSharp?.gatsbyImageData?.height}
                        image={imageData?.mobile?.file?.childImageSharp?.gatsbyImageData}
                      />
                    ) : (
                      <></>
                    )
                  )}
                </>
              )}
            </>
          )}
        </ImageContainer>
        {data?.OverlayText && (
          <HeaderCopyHeading
            dangerouslySetInnerHTML={{ __html: data.OverlayText }}
            fontSize={data?.MainFontSize}
            fontWeight={data?.MainFontWeight}
            fontFamily={data?.MainFont}
            textColor={data?.MainTextColor}
          />
        )}
      </TopLevelContainer>
    </Link>
  );
});

export default FullWidthImage;
